import { FC, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useHistory } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../../../setup'
import { UserModel } from '../../../../modules/auth/models/UserModel'
import { hasRole } from '../../../../utils/permissions'
import { UserRole } from '../../../../constants/roles'
import { KTSVG } from '../../../../../_metronic/helpers'
import { showErrorAlert } from '../../../../helpers/alert'
import { handleApiError } from '../../../../utils/handleApiError'
import { createSuggestion } from '../../../../services/suggestionService'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Required').max(150, 'Max 150 characters'),
  description: Yup.string().required('Required').max(2000, 'Max 2000 characters'),
  submitted_by_id: Yup.number().nullable(),
  wantsToLead: Yup.boolean(),
})

interface FormValues {
  title: string
  description: string
  submitted_by_id?: number | null
  wantsToLead?: boolean
}

// E1 (Suggest-to-Nobilis) member submit form. title + description required.
const SuggestExperienceForm: FC = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useHistory()
  const user = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel

  const handleSubmit = async (values: FormValues, { resetForm }: any) => {
    try {
      setLoading(true)
      await createSuggestion({
        title: values.title,
        description: values.description,
        submitted_by_id: values.submitted_by_id || undefined,
        wantsToLead: values.wantsToLead,
      })
      resetForm()
      showErrorAlert({
        title: 'Thank you',
        message: 'Your experience idea has been sent to Nobilis for review.',
      })
      navigate.goBack()
    } catch (err) {
      handleApiError(err, {
        onServerError: () =>
          showErrorAlert({
            title: 'Could not submit',
            message: 'We could not send your suggestion right now. Please try again later.',
          }),
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate.goBack()
  }

  return (
    <div className='suggest-experience-form'>
      <div className='suggest-experience-form__header'>
        <h1 className='suggest-experience-form__title'>Have an Experience in Mind?</h1>
        <p className='suggest-experience-form__subtitle'>
          We'd love to hear your ideas—whether you envision leading an experience or simply
          participating in one.
          {hasRole(user, UserRole.FINAL_USER) && (
            <>
              <br />
              <br />
              <strong>Want to lead, but not organize?</strong>
              <br />
              If you have a concept—safari, golf, sailing, or something else entirely—we'll take care of
              the logistics. We partner with top-tier luxury travel agencies to bring your vision to
              life. We organize, you lead in Nobilis Community.
              <br />
              <br />
              <strong>Prefer to participate, not lead?</strong>
              <br />
              Let us know what experience you dream of, and either Nobilis may bring it to reality.
              We're here to help make it happen.
            </>
          )}
        </p>
      </div>

      <Formik
        initialValues={{
          title: '',
          description: '',
          submitted_by_id: null,
          wantsToLead: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form className='suggest-experience-form__container'>
            <div className='suggest-experience-form__fields'>
              {/* Title (required) */}
              <div className='suggest-experience-form__field'>
                <label className='suggest-experience-form__label'>
                  Name The Suggested Experience
                </label>
                <Field name='title' className='suggest-experience-form__input' placeholder='' />
                <ErrorMessage
                  name='title'
                  component='div'
                  className='suggest-experience-form__error text-danger'
                />
              </div>

              {/* Description (required) */}
              <div className='suggest-experience-form__field'>
                <label className='suggest-experience-form__label'>Describe</label>
                <Field
                  name='description'
                  as='textarea'
                  className='suggest-experience-form__textarea'
                  placeholder=''
                />
                <p className='suggest-experience-form__helper'>
                  Tell us what you expect—your preferences, goals, and what would make this
                  experience special.
                </p>
                <ErrorMessage
                  name='description'
                  component='div'
                  className='suggest-experience-form__error text-danger'
                />
              </div>

              {/* Checkbox */}
              {hasRole(user, UserRole.FINAL_USER) && (
                <div className='suggest-experience-form__checkbox-wrapper'>
                  <label className='suggest-experience-form__checkbox'>
                    <input
                      type='checkbox'
                      checked={values.wantsToLead}
                      onChange={(e) => setFieldValue('wantsToLead', e.target.checked)}
                      name='wantsToLead'
                      id='wantsToLead'
                      className='form-check-input'
                    />
                    <span className='suggest-experience-form__checkbox-box'>
                      {values.wantsToLead && (
                        <svg width='10' height='8' viewBox='0 0 10 8' fill='none'>
                          <path
                            d='M1 4L3.5 6.5L9 1'
                            stroke='#151515'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </span>
                    <span className='suggest-experience-form__checkbox-label'>
                      <span className='suggest-experience-form__label mb-0 cursor-pointer'>
                        I wanna lead this experience
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className='suggest-experience-form__actions'>
                <button
                  type='button'
                  className='nb-btn nb-btn--outline'
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <span>cancel</span>
                </button>
                <button
                  type='submit'
                  className='tap-add-experience-2-btn-main tap-flex-center border-0 cursor-pointer'
                  disabled={loading}
                  aria-busy={loading}
                >
                  {!loading ? (
                    <div className='tap-host-button-text tap-flex-center'>
                      <span>submit</span>
                      &nbsp;
                      <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                    </div>
                  ) : (
                    <span className='indicator-progress'>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  )}
                </button>
              </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default SuggestExperienceForm
